import React, { useState } from "react";
import {
  Box,
  VStack,
  Text,
  Flex,
  Input as ChakraInput,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button as ChakraButton,
  useColorModeValue,
  Textarea,
} from "@chakra-ui/react";
import Dropdown from "../../components/ui/dropdown";
import colors from "../../utils/colors";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useUpdateDoctor } from "../../apis/doctor";
import { TDoctorInfo } from "../../apis/types";
import { setDoctorInfo } from "../../store/authSlice";
import customToast, { ToastType } from "../../components/Toast";

const PhoneAndTitleCard = () => {
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { mutate: updateDoctor, isPending } = useUpdateDoctor();
  const [isEditing, setIsEditing] = useState(false);

  const labelColor = useColorModeValue(colors.dark, "gray.200");
  const nigerianPhoneRegex = /^(\+234|0)[789][01]\d{8}$/;

  const formik = useFormik({
    initialValues: {
      name: doctorInfo?.name || "",
      title: doctorInfo?.title || "",
      phone: doctorInfo?.phoneNumber || "",
      notes: doctorInfo?.notes || "", // ✅ new field
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().required("Name is required"),
      title: yup.string().required("Title is required"),
      phone: yup
        .string()
        .matches(nigerianPhoneRegex, "Invalid Nigerian phone number")
        .required("Phone number is required"),
      notes: yup.string().max(500, "Notes cannot exceed 500 characters"), // optional validation
    }),
    onSubmit: (values) => {
      if (!doctorInfo?._id) return;

      updateDoctor(
        {
          doctorId: doctorInfo._id,
          data: {
            name: values.name,
            title: values.title,
            phoneNumber: values.phone,
            notes: values.notes, // ✅ send notes to backend
          },
        },
        {
          onSuccess: () => {
            const updatedFields: Partial<TDoctorInfo> = {
              name: values.name,
              title: values.title,
              phoneNumber: values.phone,
              notes: values.notes,
            };
            dispatch(setDoctorInfo({ ...doctorInfo, ...updatedFields }));
            sessionStorage.setItem(
              "created-doctor",
              JSON.stringify({ ...doctorInfo, ...updatedFields })
            );
            customToast("Updated successfully!", ToastType.success);
            setIsEditing(false);
          },
          onError: (err: any) => {
            customToast(
              err?.response?.data?.message || "Update failed",
              ToastType.error
            );
          },
        }
      );
    },
  });

  if (!doctorInfo) return null;

  return (
    <Box>
      {isEditing ? (
        <Box as="form" onSubmit={formik.handleSubmit}>
          <VStack spacing={4} align="stretch">
            {/* Name */}
            <FormControl
              isInvalid={formik.touched.name && !!formik.errors.name}
            >
              <FormLabel color={labelColor} fontSize="sm">
                Full Name
              </FormLabel>
              <ChakraInput
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="e.g. John Doe"
                h="40px"
              />
              <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
            </FormControl>

            {/* Title */}
            <FormControl
              isInvalid={formik.touched.title && !!formik.errors.title}
            >
              <FormLabel color={labelColor} fontSize="sm">
                Title
              </FormLabel>
              <Dropdown
                options={["Dr", "Mr", "Mrs", "Prof", "Ms"]}
                onSelect={(val) => formik.setFieldValue("title", val)}
                value={formik.values.title}
                className="!h-[40px] w-full !pl-[12px]"
                placeholder="Select title"
              />
              <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
            </FormControl>

            {/* Phone */}
            <FormControl
              isInvalid={formik.touched.phone && !!formik.errors.phone}
            >
              <FormLabel color={labelColor} fontSize="sm">
                Phone Number
              </FormLabel>
              <ChakraInput
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="e.g. 08012345678"
                h="40px"
              />
              <FormErrorMessage>{formik.errors.phone}</FormErrorMessage>
            </FormControl>

            {/* Notes */}
            <FormControl
              isInvalid={formik.touched.notes && !!formik.errors.notes}
            >
              <FormLabel color={labelColor} fontSize="sm">
                Notes
              </FormLabel>
              <Textarea
                name="notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Add any notes here..."
                resize="vertical"
                minH="80px"
              />
              <FormErrorMessage>{formik.errors.notes}</FormErrorMessage>
            </FormControl>

            {/* Actions */}
            <Flex justifyContent="flex-end" gap={2} mt={2}>
              <ChakraButton
                size="sm"
                variant="ghost"
                onClick={() => {
                  formik.resetForm();
                  setIsEditing(false);
                }}
              >
                Cancel
              </ChakraButton>
              <ChakraButton
                size="sm"
                type="submit"
                isLoading={isPending}
                bg={colors.primary}
                color="white"
                _hover={{ bg: colors.secondaryBlack || colors.primary }}
                isDisabled={!formik.dirty}
              >
                Save
              </ChakraButton>
            </Flex>
          </VStack>
        </Box>
      ) : (
        <Box>
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            Contact Information
          </Text>
          <VStack align="start" spacing={2}>
            <Text>
              <strong>Name:</strong> {doctorInfo.name || "Not set"}
            </Text>
            <Text>
              <strong>Title:</strong> {doctorInfo.title || "Not set"}
            </Text>
            <Text>
              <strong>Phone:</strong> {doctorInfo.phoneNumber || "Not set"}
            </Text>
            <Text>
              <strong>Notes:</strong> {doctorInfo.notes || "Not set"}
            </Text>
          </VStack>
          <Flex mt={4} justifyContent="flex-end">
            <Box
              as="button"
              onClick={() => setIsEditing(true)}
              fontSize="sm"
              color="blue.500"
            >
              Edit Contact Info
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default PhoneAndTitleCard;
